import { addDaysIso, zonedParts, zonedToUtc } from './time.js';
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_WORD = '(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';
/** Words after "from" that are never a person: "a quote from the supplier" keeps "from the supplier" as text. */
const NOT_A_NAME = new Set(['the', 'a', 'an', 'my', 'our', 'your', 'their', 'his', 'her', 'this', 'that', 'last', 'next', 'yesterday', 'today', 'home', 'here', 'there', 'now', 'scratch']);
const monthIndex = (word) => MONTHS.findIndex((m) => m.startsWith(word.toLowerCase().slice(0, 3)));
const iso = (d) => d.toISOString();
const dayStart = (date, tz) => {
    const [y, m, d] = date.split('-').map(Number);
    return zonedToUtc(y, m, d, 0, 0, tz);
};
const pad = (n) => String(n).padStart(2, '0');
/** A calendar date from "2026-10-03", "3 oct", "oct 3", "3 October 2025". The year defaults to the most recent past one. */
function parseDate(text, today) {
    const t = text.trim().toLowerCase();
    let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (m)
        return `${m[1]}-${m[2]}-${m[3]}`;
    m = new RegExp(`^(\\d{1,2})(?:st|nd|rd|th)?\\s+${MONTH_WORD}(?:\\s+(\\d{4}))?$`).exec(t) ?? null;
    let day = null;
    let month = -1;
    let year = null;
    if (m) {
        day = Number(m[1]);
        month = monthIndex(m[2]);
        year = m[3] ? Number(m[3]) : null;
    }
    else {
        m = new RegExp(`^${MONTH_WORD}\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?$`).exec(t);
        if (!m)
            return null;
        month = monthIndex(m[1]);
        day = Number(m[2]);
        year = m[3] ? Number(m[3]) : null;
    }
    if (month < 0 || !day || day > 31)
        return null;
    if (year === null) {
        const later = month + 1 > today.month || (month + 1 === today.month && day > today.day);
        year = later ? today.year - 1 : today.year;
    }
    const check = new Date(Date.UTC(year, month, day));
    if (check.getUTCMonth() !== month)
        return null;
    return `${year}-${pad(month + 1)}-${pad(day)}`;
}
const shortDate = (date) => {
    const [, m, d] = date.split('-').map(Number);
    return `${d} ${MONTH_SHORT[m - 1]}`;
};
export function parseSearchInput(input, now, timeZone) {
    let text = ` ${input.replace(/\s+/g, ' ').trim()} `;
    const out = { text: '', from: null, in: null, range: null };
    const take = (re, handle) => {
        const m = re.exec(text);
        if (m && handle(m))
            text = `${text.slice(0, m.index)} ${text.slice(m.index + m[0].length)}`;
    };
    // People: "from:anna", "from @anna", "from anna" (one word), 'from "anna lee"'.
    take(/\sfrom:\s?@?("[^"]{1,60}"|[\p{L}\p{N}._'-]{1,60})(?=\s)/iu, (m) => {
        out.from = { name: m[1].replace(/"/g, '').toLowerCase(), raw: m[0].trim() };
        return true;
    });
    if (!out.from)
        take(/\sfrom\s+@?("[^"]{1,60}"|[\p{L}][\p{L}\p{N}._'-]{0,59})(?=\s)/iu, (m) => {
            const name = m[1].replace(/"/g, '').toLowerCase();
            if (NOT_A_NAME.has(name))
                return false;
            out.from = { name, raw: m[0].trim() };
            return true;
        });
    // Channels need "in:" or "#", because "in" is an everyday word ("in October", "in progress").
    take(/\s(?:in:\s?#?|in\s+#|#)([\p{L}\p{N}][\p{L}\p{N}_-]{0,79})(?=\s)/iu, (m) => {
        out.in = { name: m[1].toLowerCase(), raw: m[0].trim() };
        return true;
    });
    // Dates, in the person's time zone. The first phrase found wins.
    const p = zonedParts(now, timeZone);
    const todayIso = `${p.year}-${pad(p.month)}-${pad(p.day)}`;
    const mondayIso = addDaysIso(todayIso, -((p.weekday + 6) % 7));
    const monthStart = (y, m) => `${y}-${pad(m)}-01`;
    const range = (raw, label, after, before) => {
        out.range = { after: after ? iso(dayStart(after, timeZone)) : null, before: before ? iso(dayStart(before, timeZone)) : null, label, raw: raw.trim() };
        return true;
    };
    const relative = [
        [/\stoday(?=\s)/i, () => ['Today', todayIso, null]],
        [/\syesterday(?=\s)/i, () => ['Yesterday', addDaysIso(todayIso, -1), todayIso]],
        [/\sthis\s+week(?=\s)/i, () => ['This week', mondayIso, null]],
        [/\slast\s+week(?=\s)/i, () => ['Last week', addDaysIso(mondayIso, -7), mondayIso]],
        [/\sthis\s+month(?=\s)/i, () => ['This month', monthStart(p.year, p.month), null]],
        [
            /\slast\s+month(?=\s)/i,
            () => {
                const y = p.month === 1 ? p.year - 1 : p.year;
                const m = p.month === 1 ? 12 : p.month - 1;
                return ['Last month', monthStart(y, m), monthStart(p.year, p.month)];
            },
        ],
    ];
    for (const [re, make] of relative) {
        if (out.range)
            break;
        take(re, (m) => {
            const [label, after, before] = make();
            return range(m[0], label, after, before);
        });
    }
    if (!out.range)
        take(/\s(?:in\s+the\s+)?(?:last|past)\s+(\d{1,3})\s+days(?=\s)/i, (m) => {
            const n = Math.min(Number(m[1]), 366);
            return n > 0 && range(m[0], `Last ${n} days`, addDaysIso(todayIso, -(n - 1)), null);
        });
    if (!out.range)
        take(new RegExp(`\\s(before|after|since|on)\\s+((?:\\d{4}-\\d{2}-\\d{2})|(?:\\d{1,2}(?:st|nd|rd|th)?\\s+${MONTH_WORD}(?:\\s+\\d{4})?)|(?:${MONTH_WORD}\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,?\\s+\\d{4})?))(?=\\s)`, 'i'), (m) => {
            const date = parseDate(m[2], p);
            if (!date)
                return false;
            const word = m[1].toLowerCase();
            if (word === 'before')
                return range(m[0], `Before ${shortDate(date)}`, null, date);
            if (word === 'on')
                return range(m[0], shortDate(date), date, addDaysIso(date, 1));
            return range(m[0], `Since ${shortDate(date)}`, date, null);
        });
    if (!out.range)
        take(new RegExp(`\\s(?:in|during)\\s+${MONTH_WORD}(?:\\s+(\\d{4}))?(?=\\s)`, 'i'), (m) => {
            const month = monthIndex(m[1]) + 1;
            const year = m[2] ? Number(m[2]) : month > p.month ? p.year - 1 : p.year;
            const next = month === 12 ? monthStart(year + 1, 1) : monthStart(year, month + 1);
            const label = `${MONTHS[month - 1][0].toUpperCase()}${MONTHS[month - 1].slice(1)}${year === p.year ? '' : ` ${year}`}`;
            return range(m[0], label, monthStart(year, month), next);
        });
    out.text = text.replace(/\s+/g, ' ').trim();
    return out;
}
export const NO_FILTERS = Object.freeze({ from: null, in: null, range: null });
const lower = (s) => s.trim().toLowerCase().replace(/\s+/g, ' ');
/**
 * The one person a typed name means: their full name or their first name, ignoring case ("me" is you).
 * Two people called Sam is no match at all, so a guess never narrows the search to the wrong person.
 */
export function resolvePerson(name, people, selfId) {
    const n = lower(name.replace(/[._]/g, ' '));
    if (!n)
        return null;
    const unique = (list) => {
        const ids = new Set(list.map((p) => p.id));
        return ids.size === 1 ? list[0] : null;
    };
    if (n === 'me' && selfId)
        return people.find((p) => p.id === selfId) ?? null;
    const full = people.filter((p) => lower(p.name) === n);
    if (full.length)
        return unique(full);
    return unique(people.filter((p) => lower(p.name).split(' ')[0] === n));
}
/** The one channel (not a direct message) with this name, ignoring case and a leading "#". */
export function resolveChannel(name, channels) {
    const n = lower(name.replace(/^#/, ''));
    if (!n)
        return null;
    const found = channels.filter((c) => !c.dm && lower(c.name) === n);
    return new Set(found.map((c) => c.id)).size === 1 ? found[0] : null;
}
/**
 * Reads what the person typed into text plus chips. A name that matches no one (or several people) stays
 * in the text where it was typed, so it is still searched for; a new chip replaces one of the same kind.
 */
export function resolveSearchInput(input, now, timeZone, dir, current = NO_FILTERS) {
    const parsed = parseSearchInput(input, now, timeZone);
    const from = parsed.from ? resolvePerson(parsed.from.name, dir.people, dir.selfId) : null;
    const inChannel = parsed.in ? resolveChannel(parsed.in.name, dir.channels) : null;
    const taken = [];
    const kept = [];
    (from ? taken : kept).push(...(parsed.from ? [parsed.from.raw] : []));
    (inChannel ? taken : kept).push(...(parsed.in ? [parsed.in.raw] : []));
    if (parsed.range)
        taken.push(parsed.range.raw);
    // Take the chip words out of what was typed, so the rest stays in the order it was written.
    let text = ` ${input.replace(/\s+/g, ' ').trim()} `;
    for (const raw of taken) {
        const i = text === null ? -1 : text.indexOf(raw);
        text = i < 0 || text === null ? null : `${text.slice(0, i)} ${text.slice(i + raw.length)}`;
    }
    const rest = (text ?? [parsed.text, ...kept].join(' ')).replace(/\s+/g, ' ').trim();
    const filters = {
        from: from ? { id: from.id, name: from.name } : current.from,
        in: inChannel ? { id: inChannel.id, name: inChannel.name, ...(inChannel.dm ? { dm: true } : {}) } : current.in,
        range: parsed.range ? { after: parsed.range.after, before: parsed.range.before, label: parsed.range.label } : current.range,
    };
    return { text: rest, filters, changed: taken.length > 0 };
}
/** The date choices offered without typing, in order. */
export const DATE_PRESETS = [
    { key: 'today', label: 'Today', phrase: 'today' },
    { key: 'yesterday', label: 'Yesterday', phrase: 'yesterday' },
    { key: 'last7', label: 'Last 7 days', phrase: 'last 7 days' },
    { key: 'last30', label: 'Last 30 days', phrase: 'last 30 days' },
    { key: 'thisMonth', label: 'This month', phrase: 'this month' },
    { key: 'lastMonth', label: 'Last month', phrase: 'last month' },
];
/** A preset as a range in the person's time zone, read the same way as typed words. */
export function datePresetRange(key, now, timeZone) {
    const preset = DATE_PRESETS.find((p) => p.key === key);
    const r = parseSearchInput(preset.phrase, now, timeZone).range;
    return { after: r.after, before: r.before, label: preset.label };
}
/** The search request's filter fields; chips that are not set are left out. */
export function filterQuery(f) {
    return {
        ...(f.from ? { from: f.from.id } : {}),
        ...(f.in ? { in: f.in.id } : {}),
        ...(f.range?.after ? { after: f.range.after } : {}),
        ...(f.range?.before ? { before: f.range.before } : {}),
    };
}
export const hasFilters = (f) => !!(f.from || f.in || f.range);
/** What a chip says: "From Mia Hassan", "In #acme", "In DM with Dev Kapoor", "Last week". */
export function filterLabel(kind, f) {
    if (kind === 'from')
        return f.from ? `From ${f.from.name}` : '';
    if (kind === 'in')
        return f.in ? `In ${f.in.dm ? `DM with ${f.in.name}` : `#${f.in.name}`}` : '';
    return f.range?.label ?? '';
}
/** A range label as words in a sentence: "last week", "on 3 Oct", "in October", "before 3 Oct". */
function rangeWords(label) {
    if (/^\d/.test(label))
        return `on ${label}`;
    if (MONTHS.some((m) => label.toLowerCase().startsWith(m)))
        return `in ${label}`;
    return label.charAt(0).toLowerCase() + label.slice(1);
}
/**
 * The search in words, for asking Chapi to find it: `invoice, from Mia Hassan, in #acme, last week`.
 * Dates keep the chip's words ("last week"), which Chapi reads in the person's time zone too.
 */
export function describeSearch(text, f) {
    const parts = [text.trim() ? `“${text.trim()}”` : ''];
    if (f.from)
        parts.push(`from ${f.from.name}`);
    if (f.in)
        parts.push(f.in.dm ? `in my DM with ${f.in.name}` : `in #${f.in.name}`);
    if (f.range)
        parts.push(rangeWords(f.range.label));
    return parts.filter(Boolean).join(', ');
}
/**
 * The people and channels names resolve against: members (staff only: guests have no directory) plus the
 * people in your DMs, and every channel in your sidebar, DMs under the other person's name.
 */
export function searchDirectory(members, sidebar, selfId) {
    const people = new Map();
    for (const m of members ?? [])
        people.set(m.userId, { id: m.userId, name: m.name });
    for (const c of sidebar?.dms ?? [])
        if (c.dmWith && !people.has(c.dmWith.id))
            people.set(c.dmWith.id, { id: c.dmWith.id, name: c.dmWith.name });
    const channels = [];
    for (const c of [...(sidebar?.clients.flatMap((g) => g.channels) ?? []), ...(sidebar?.team ?? [])])
        channels.push({ id: c.id, name: c.name });
    for (const c of sidebar?.dms ?? [])
        if (c.dmWith)
            channels.push({ id: c.id, name: c.dmWith.name, dm: true });
    return { people: [...people.values()], channels, selfId };
}
/** The order chips are shown in; Backspace in an empty box removes the last one. */
export const FILTER_ORDER = ['from', 'in', 'range'];
export const filterKinds = (f) => FILTER_ORDER.filter((k) => f[k] !== null);
export const withoutFilter = (f, kind) => ({ ...f, [kind]: null });
/** Backspace at the start of an empty box: the last chip goes. */
export function withoutLastFilter(f) {
    const last = filterKinds(f).at(-1);
    return last ? withoutFilter(f, last) : f;
}
/** What a chip's remove button says to a screen reader: "Remove filter: from Mia Hassan". */
export function removeFilterLabel(kind, f) {
    if (kind === 'from')
        return `Remove filter: from ${f.from?.name ?? ''}`;
    if (kind === 'in')
        return `Remove filter: in ${f.in?.dm ? `DM with ${f.in.name}` : `#${f.in?.name ?? ''}`}`;
    return `Remove filter: ${f.range?.label.toLowerCase() ?? ''}`;
}
//# sourceMappingURL=search-parse.js.map